<template>
  <v-container fluid class="bible-page">
    <div class="bible-page__wrap">
      <div v-if="loadingInitial" class="bible-state">
        <v-progress-circular indeterminate size="48" width="4" color="#6f8063"></v-progress-circular>
        <span>Preparando o estudo de hoje...</span>
      </div>

      <template v-else-if="!sessionId">
        <section class="study-picker">
          <div class="study-picker__hero">
            <div class="study-picker__mark">
              <v-icon color="#6f8063">mdi-book-cross</v-icon>
            </div>
            <span>Estudo Biblico</span>
            <h1>Escolha o livro e capitulo para o momento de hoje.</h1>
            <p>Um espaco tranquilo para leitura, comunhao e reflexao a dois.</p>
          </div>

          <div class="study-picker__grid">
            <article class="study-picker__card">
              <div class="study-picker__card-header">
                <div>
                  <span>Nova sala</span>
                  <h2>Preparar estudo</h2>
                </div>
                <v-icon color="#8b6d32">mdi-leaf</v-icon>
              </div>

              <v-select
                v-model="selectedBookId"
                :items="books"
                item-text="name"
                item-value="id"
                label="Livro"
                placeholder="Selecione o livro"
                outlined
                :loading="loadingBooks"
                :disabled="loadingBooks || starting"
                class="study-picker__field"
                @change="handleBookChange"
              ></v-select>

              <v-select
                v-model="selectedChapterId"
                :items="chapters"
                item-text="label"
                item-value="id"
                label="Capitulo"
                placeholder="Selecione o capitulo"
                outlined
                :loading="loadingChapters"
                :disabled="!selectedBookId || loadingChapters || starting"
                class="study-picker__field"
              ></v-select>

              <v-btn
                block
                color="#6f8063"
                dark
                depressed
                large
                class="study-picker__button"
                :loading="starting"
                :disabled="!selectedChapterId || starting"
                @click="startStudy"
              >
                <v-icon left>mdi-door-open</v-icon>
                Iniciar sala de estudo
              </v-btn>
            </article>

            <article v-if="suggestedChapterTitle" class="study-picker__suggestion">
              <span>Proximo sugerido</span>
              <h3>{{ suggestedChapterTitle }}</h3>
              <p>Use a sugestao do backend ou escolha outro livro livremente.</p>
              <v-btn
                outlined
                color="#6f8063"
                class="study-picker__suggestion-button"
                :loading="applyingSuggestion"
                :disabled="loadingBooks || starting"
                @click="useSuggestion"
              >
                <v-icon left>mdi-auto-fix</v-icon>
                Usar sugestao
              </v-btn>
            </article>
          </div>
        </section>

        <v-alert v-if="pageError" text dense type="warning" class="mt-4">
          {{ pageError }}
        </v-alert>
      </template>

      <template v-else>
        <BibleStudyHeader
          :chapter-title="chapterTitle"
          :session-id="sessionId"
          :user-online="enteredStudy && websocketStatus === 'CONNECTED'"
          :partner-online="partnerOnline"
          :completing="completing"
          :completed="completed"
          @complete="completeReading"
        />

        <v-alert v-if="pageError" text dense type="warning" class="mt-4">
          {{ pageError }}
        </v-alert>

        <section v-if="!enteredStudy" class="entry-panel">
          <div>
            <span>Estudo em andamento</span>
            <p>Entre na sala para sincronizar presenca, anotacoes e chamada.</p>
          </div>
          <v-btn
            color="#6f8063"
            dark
            depressed
            :loading="entering"
            @click="enterStudy"
          >
            <v-icon left>mdi-door-open</v-icon>
            Entrar na sala
          </v-btn>
        </section>

        <div class="room-layout">
            <main class="room-main">
              <section class="room-section">
                <div class="room-section__title">
                  <span>Chamada devocional</span>
                  <small>{{ callStatusLabel }}</small>
                </div>

                <BibleVideoPanel
                  :local-stream="localStream"
                  :remote-stream="remoteStream"
                  :partner-online="partnerOnline"
                  :camera-error="cameraError"
                />

                <BibleCallControls
                  :call-active="callActive"
                  :audio-enabled="audioEnabled"
                  :video-enabled="videoEnabled"
                  :video-available="localVideoAvailable"
                  :loading="startingCall"
                  :disabled="!enteredStudy"
                  :remote-waiting="remoteCallWaiting"
                  @activate="activateCall"
                  @toggle-audio="toggleAudio"
                  @toggle-video="toggleVideo"
                  @end="endCall"
                />
              </section>

              <section class="notes-grid">
                <BibleNotePanel
                  title="Minhas anotacoes"
                  subtitle="Salvamento automatico enquanto voce escreve"
                  :value="myNote"
                  :disabled="!enteredStudy"
                  :saving="noteSaving"
                  placeholder="Registre observacoes, oracoes e pontos que tocaram seu coracao..."
                  @input="handleMyNoteInput"
                />

                <BibleNotePanel
                  title="Anotacoes da parceira"
                  subtitle="Somente leitura, sincronizada em tempo real"
                  :value="partnerNote"
                  readonly
                  :disabled="!enteredStudy"
                  placeholder="As anotacoes dela aparecem aqui quando forem compartilhadas."
                />
              </section>
            </main>

            <BibleAiAssistant :session-id="sessionId" :chapter-title="chapterTitle" />
        </div>
      </template>
    </div>
  </v-container>
</template>

<script>
import authService from '@/services/authService';
import bibleStudyService from '@/services/bibleStudyService';
import BibleStompClient, { createClientEventId } from '@/services/bibleWebSocketService';
import BibleWebRtcClient from '@/services/bibleWebRtcService';
import BibleAiAssistant from './BibleAiAssistant.vue';
import BibleCallControls from './BibleCallControls.vue';
import BibleNotePanel from './BibleNotePanel.vue';
import BibleStudyHeader from './BibleStudyHeader.vue';
import BibleVideoPanel from './BibleVideoPanel.vue';

export default {
  name: 'BibleStudyPage',
  components: {
    BibleAiAssistant,
    BibleCallControls,
    BibleNotePanel,
    BibleStudyHeader,
    BibleVideoPanel,
  },
  data() {
    return {
      currentStudy: null,
      loadingInitial: true,
      starting: false,
      entering: false,
      completing: false,
      enteredStudy: false,
      completed: false,
      pageError: '',
      books: [],
      chapters: [],
      selectedBookId: null,
      selectedChapterId: null,
      loadingBooks: false,
      loadingChapters: false,
      applyingSuggestion: false,
      partnerOnline: false,
      websocketStatus: 'DISCONNECTED',
      wsClient: null,
      unsubscribeFns: [],
      pendingClientEventIds: new Set(),
      myNote: '',
      partnerNote: '',
      noteSaving: false,
      noteTimer: null,
      localStream: null,
      remoteStream: null,
      startingCall: false,
      audioEnabled: true,
      videoEnabled: true,
      cameraError: '',
      remoteCallWaiting: false,
      webRtcClient: null,
    };
  },
  computed: {
    sessionId() {
      return this.extractSessionId(this.currentStudy);
    },
    chapter() {
      const study = this.currentStudy || {};
      return study.chapter || study.currentChapter || study.session?.chapter || study.capitulo || {};
    },
    suggestedChapter() {
      const study = this.currentStudy || {};
      return study.suggestedChapter
        || study.nextChapter
        || study.nextSuggestedChapter
        || study.proximoCapitulo
        || (!this.sessionId ? study.chapter || study.currentChapter || study.capitulo : {})
        || {};
    },
    chapterTitle() {
      const bookName = this.resolveBookName(this.chapter)
        || this.currentStudy?.bookName
        || this.currentStudy?.livroNome
        || this.resolveBookName(this.currentStudy)
        || 'Capitulo';
      const chapterNumber = this.chapter.number
        || this.chapter.chapterNumber
        || this.chapter.numero
        || this.currentStudy?.chapterNumber
        || this.currentStudy?.capituloNumero
        || '';

      return `${bookName} ${chapterNumber}`.trim();
    },
    suggestedChapterTitle() {
      return this.formatChapterTitle(this.suggestedChapter);
    },
    currentUserId() {
      const payload = authService.token.decode() || {};
      return payload.userId || payload.id || payload.usuarioId || payload.sub || null;
    },
    callActive() {
      return !!this.localStream;
    },
    localVideoAvailable() {
      return !!this.localStream?.getVideoTracks?.().length;
    },
    callStatusLabel() {
      if (!this.enteredStudy) return 'Entre no estudo para iniciar a chamada';
      if (this.callActive && this.remoteStream) return 'Chamada conectada';
      if (this.callActive) return 'Aguardando parceira entrar...';
      if (this.remoteCallWaiting) return 'Parceira aguardando chamada';
      return 'Voz e video desligados';
    },
  },
  created() {
    this.loadCurrentStudy();
    window.addEventListener('beforeunload', this.cleanup);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.cleanup);
    this.cleanup();
  },
  methods: {
    extractSessionId(study) {
      if (!study) return null;
      return study.sessionId || study.session?.id || study.currentSession?.id || null;
    },
    async loadCurrentStudy() {
      this.loadingInitial = true;
      this.pageError = '';

      try {
        const study = await bibleStudyService.buscarEstudoAtual();
        this.applyStudy(study);
        if (!this.extractSessionId(study)) {
          await this.loadBooks();
        }
      } catch (error) {
        console.error('Erro ao carregar estudo biblico atual:', error);
        this.pageError = 'Nao foi possivel carregar o estudo biblico agora.';
      } finally {
        this.loadingInitial = false;
      }
    },
    applyStudy(study) {
      this.currentStudy = study || {};
      this.completed = !!(study?.completed || study?.myCompleted || study?.completedByMe);
    },
    normalizeList(payload, keys) {
      if (Array.isArray(payload)) return payload;

      for (const key of keys) {
        if (Array.isArray(payload?.[key])) {
          return payload[key];
        }
      }

      return [];
    },
    normalizeBook(book) {
      const id = book?.id || book?.bookId || book?.livroId;
      const name = book?.name || book?.nome || book?.title || book?.titulo || '';

      return {
        ...book,
        id,
        name,
      };
    },
    normalizeChapter(chapter) {
      const id = chapter?.id || chapter?.chapterId || chapter?.capituloId;
      const number = chapter?.number || chapter?.chapterNumber || chapter?.numero || chapter?.capitulo || '';

      return {
        ...chapter,
        id,
        number,
        label: number ? `Capitulo ${number}` : (chapter?.title || chapter?.titulo || 'Capitulo'),
      };
    },
    async loadBooks() {
      this.loadingBooks = true;

      try {
        const response = await bibleStudyService.listarLivros();
        this.books = this.normalizeList(response, ['books', 'livros', 'data', 'content'])
          .map(this.normalizeBook)
          .filter((book) => book.id && book.name);
      } catch (error) {
        console.error('Erro ao carregar livros biblicos:', error);
        this.pageError = 'Nao foi possivel carregar os livros biblicos agora.';
      } finally {
        this.loadingBooks = false;
      }
    },
    async handleBookChange(bookId) {
      this.selectedChapterId = null;
      this.chapters = [];

      if (bookId) {
        await this.loadChapters(bookId);
      }
    },
    async loadChapters(bookId) {
      this.loadingChapters = true;

      try {
        const response = await bibleStudyService.listarCapitulos(bookId);
        this.chapters = this.normalizeList(response, ['chapters', 'capitulos', 'data', 'content'])
          .map(this.normalizeChapter)
          .filter((chapter) => chapter.id);
      } catch (error) {
        console.error('Erro ao carregar capitulos biblicos:', error);
        this.pageError = 'Nao foi possivel carregar os capitulos desse livro.';
      } finally {
        this.loadingChapters = false;
      }
    },
    formatChapterTitle(chapter) {
      if (!chapter || Object.keys(chapter).length === 0) return '';

      const bookName = this.resolveBookName(chapter);
      const chapterNumber = chapter.number || chapter.chapterNumber || chapter.numero || chapter.capitulo || '';

      return `${bookName || 'Capitulo'} ${chapterNumber}`.trim();
    },
    resolveBookName(source) {
      if (!source) return '';
      if (typeof source.book === 'string') return source.book;
      if (typeof source.livro === 'string') return source.livro;

      return source.bookName
        || source.livroNome
        || source.book?.name
        || source.book?.nome
        || source.livro?.name
        || source.livro?.nome
        || '';
    },
    async useSuggestion() {
      if (!this.suggestedChapterTitle) return;

      this.applyingSuggestion = true;
      this.pageError = '';

      try {
        if (!this.books.length) {
          await this.loadBooks();
        }

        const suggested = this.suggestedChapter;
        const suggestedBookId = suggested.bookId
          || suggested.livroId
          || suggested.book?.id
          || suggested.livro?.id
          || this.currentStudy?.bookId
          || this.currentStudy?.livroId;
        const suggestedBookName = this.resolveBookName(suggested);

        const book = this.books.find((item) => String(item.id) === String(suggestedBookId))
          || this.books.find((item) => item.name.toLowerCase() === String(suggestedBookName).toLowerCase());

        if (!book) {
          this.pageError = 'Nao foi possivel encontrar o livro sugerido na lista.';
          return;
        }

        this.selectedBookId = book.id;
        await this.loadChapters(book.id);

        const suggestedChapterId = suggested.id || suggested.chapterId || suggested.capituloId || this.currentStudy?.chapterId;
        const suggestedChapterNumber = suggested.number || suggested.chapterNumber || suggested.numero || suggested.capitulo || '';
        const chapter = this.chapters.find((item) => String(item.id) === String(suggestedChapterId))
          || this.chapters.find((item) => String(item.number) === String(suggestedChapterNumber));

        if (!chapter) {
          this.pageError = 'Nao foi possivel encontrar o capitulo sugerido na lista.';
          return;
        }

        this.selectedChapterId = chapter.id;
      } finally {
        this.applyingSuggestion = false;
      }
    },
    async startStudy() {
      if (!this.selectedChapterId) {
        this.pageError = 'Escolha um livro e um capitulo antes de iniciar.';
        return;
      }

      this.starting = true;
      this.pageError = '';

      try {
        const study = await bibleStudyService.iniciarEstudo({
          chapterId: this.selectedChapterId,
        });

        this.applyStudy(study);

        if (!this.sessionId) {
          await this.loadCurrentStudy();
        }

        await this.enterStudy();
        this.$toast.success('Estudo de hoje iniciado.');
      } catch (error) {
        console.error('Erro ao iniciar estudo biblico:', error);
        this.pageError = error.response?.data?.message || 'Nao foi possivel iniciar o estudo de hoje.';
      } finally {
        this.starting = false;
      }
    },
    async enterStudy() {
      if (!this.sessionId || this.enteredStudy || this.entering) return;

      this.entering = true;
      this.pageError = '';

      try {
        this.createWebRtcClient();

        this.wsClient = new BibleStompClient();
        this.wsClient.setStatusHandler((status) => {
          this.websocketStatus = status;
        });

        await this.wsClient.connect();
        this.subscribeToStudyTopics();
        this.sendPresence('JOIN_SESSION');
        this.enteredStudy = true;

        await this.loadNotes();
      } catch (error) {
        console.error('Erro ao entrar no estudo biblico:', error);
        this.pageError = error?.message
          ? `Nao foi possivel conectar ao estudo em tempo real. ${error.message}`
          : 'Nao foi possivel conectar ao estudo em tempo real.';
        this.cleanupWebSocket();
      } finally {
        this.entering = false;
      }
    },
    subscribeToStudyTopics() {
      this.unsubscribeFns.forEach((unsubscribe) => unsubscribe());
      this.unsubscribeFns = [
        this.wsClient.subscribe(`/topic/study/${this.sessionId}`, this.handleIncomingNote),
        this.wsClient.subscribe(`/topic/study/${this.sessionId}/presence`, this.handlePresence),
        this.wsClient.subscribe(`/topic/study/${this.sessionId}/call`, this.handleCallSignal),
      ];
    },
    async loadNotes() {
      try {
        const [allNotes, myNote] = await Promise.all([
          bibleStudyService.buscarNotas(this.sessionId),
          bibleStudyService.buscarMinhaNota(this.sessionId),
        ]);

        this.myNote = this.extractNoteContent(myNote);
        this.partnerNote = this.extractPartnerNote(allNotes);
      } catch (error) {
        console.error('Erro ao carregar notas biblicas:', error);
        this.$toast.warning('Nao foi possivel carregar as anotacoes agora.');
      }
    },
    extractNoteContent(note) {
      if (!note) return '';
      if (typeof note === 'string') return note;
      return note.content || note.conteudo || note.note || '';
    },
    extractPartnerNote(notes) {
      const noteList = Array.isArray(notes) ? notes : (notes?.notes || notes?.data || []);
      const currentUserId = String(this.currentUserId || '');
      const partnerNote = noteList.find((note) => {
        const userId = String(note.userId || note.authorId || note.usuarioId || note.ownerId || '');
        return userId && userId !== currentUserId;
      }) || noteList.find((note) => this.extractNoteContent(note) !== this.myNote);

      return this.extractNoteContent(partnerNote);
    },
    handleMyNoteInput(value) {
      this.myNote = value;
      if (!this.enteredStudy) return;

      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => {
        this.saveMyNote();
      }, 700);
    },
    async saveMyNote() {
      if (!this.sessionId) return;

      this.noteSaving = true;
      const clientEventId = createClientEventId('note');
      this.trackClientEvent(clientEventId);

      try {
        if (this.wsClient?.connected) {
          this.wsClient.send(`/app/study/${this.sessionId}/write`, {
            content: this.myNote,
            clientEventId,
          });
        }

        await bibleStudyService.salvarMinhaNota(this.sessionId, this.myNote);
      } catch (error) {
        console.error('Erro ao salvar anotacao biblica:', error);
        this.$toast.warning('Nao foi possivel salvar sua anotacao agora.');
      } finally {
        this.noteSaving = false;
      }
    },
    handleIncomingNote(payload) {
      if (!payload) return;
      if (this.isOwnClientEvent(payload.clientEventId)) return;

      const content = this.extractNoteContent(payload);
      if (!content && !payload.notes) return;

      const senderId = String(payload.userId || payload.authorId || payload.usuarioId || payload.ownerId || '');
      if (senderId && String(this.currentUserId || '') === senderId) return;

      if (payload.notes) {
        this.partnerNote = this.extractPartnerNote(payload.notes);
        return;
      }

      this.partnerNote = content;
    },
    handlePresence(payload) {
      if (!payload || this.isOwnClientEvent(payload.clientEventId)) return;

      const senderId = String(payload.userId || payload.usuarioId || payload.ownerId || '');
      if (senderId && String(this.currentUserId || '') === senderId) return;

      if (Array.isArray(payload.onlineUsers)) {
        const currentUserId = String(this.currentUserId || '');
        this.partnerOnline = payload.onlineUsers.some((id) => String(id) !== currentUserId);
        return;
      }

      if (payload.online !== undefined) {
        this.partnerOnline = !!payload.online;
        return;
      }

      if (payload.type === 'JOIN_SESSION') {
        this.partnerOnline = true;
      } else if (payload.type === 'LEAVE_SESSION') {
        this.partnerOnline = false;
      }
    },
    sendPresence(type) {
      if (!this.wsClient?.connected || !this.sessionId) return;

      const clientEventId = createClientEventId('presence');
      this.trackClientEvent(clientEventId);
      this.wsClient.send(`/app/study/${this.sessionId}/presence`, {
        type,
        clientEventId,
      });
    },
    createWebRtcClient() {
      if (this.webRtcClient) return;

      this.webRtcClient = new BibleWebRtcClient({
        sendSignal: this.sendCallSignal,
        onLocalStream: (stream) => {
          this.localStream = stream;
        },
        onRemoteStream: (stream) => {
          this.remoteStream = stream;
          if (stream) {
            this.remoteCallWaiting = false;
          }
        },
        onStateChange: this.handleCallState,
        onError: this.handleCallError,
      });
    },
    sendCallSignal(payload) {
      if (!this.wsClient?.connected || !this.sessionId) return;

      const clientEventId = payload.clientEventId || createClientEventId('call');
      this.trackClientEvent(clientEventId);
      this.wsClient.send(`/app/study/${this.sessionId}/call/signal`, {
        ...payload,
        clientEventId,
      });
    },
    async handleCallSignal(payload) {
      if (!payload || this.isOwnClientEvent(payload.clientEventId)) return;
      this.createWebRtcClient();
      await this.webRtcClient.handleSignal(payload);
    },
    handleCallState(state) {
      if (['remote-waiting', 'incoming-offer'].includes(state)) {
        this.remoteCallWaiting = true;
      }

      if (state === 'remote-left') {
        this.remoteCallWaiting = false;
      }
    },
    handleCallError(error) {
      console.error('Erro WebRTC:', error);
      this.cameraError = 'Nao foi possivel manter a chamada. Verifique permissoes e conexao.';
    },
    async activateCall() {
      if (!this.enteredStudy) {
        await this.enterStudy();
      }

      this.startingCall = true;
      this.cameraError = '';

      try {
        this.createWebRtcClient();
        const mediaStatus = await this.webRtcClient.start();
        this.audioEnabled = true;
        this.videoEnabled = !!mediaStatus?.stream?.getVideoTracks?.().length;
        if (mediaStatus?.audioOnly && mediaStatus.message) {
          this.cameraError = mediaStatus.message;
        }
      } catch (error) {
        if (error?.name === 'NotAllowedError') {
          this.cameraError = 'Permissao de camera ou microfone negada pelo navegador.';
        } else if (error?.name === 'NotReadableError') {
          this.cameraError = 'Camera ou microfone ja esta em uso por outro aplicativo.';
        } else if (error?.name === 'NoAudioInputError') {
          this.cameraError = 'Nenhum microfone foi encontrado. Verifique os dispositivos conectados.';
        } else if (error?.name === 'NotFoundError') {
          this.cameraError = 'Dispositivo de audio ou video nao encontrado.';
        } else {
          this.cameraError = 'Nao foi possivel ativar voz e video.';
        }
      } finally {
        this.startingCall = false;
      }
    },
    toggleAudio() {
      this.audioEnabled = !this.audioEnabled;
      this.webRtcClient?.setAudioEnabled(this.audioEnabled);
    },
    toggleVideo() {
      if (!this.localVideoAvailable) return;
      this.videoEnabled = !this.videoEnabled;
      this.webRtcClient?.setVideoEnabled(this.videoEnabled);
    },
    endCall() {
      this.webRtcClient?.end();
      this.localStream = null;
      this.remoteStream = null;
      this.remoteCallWaiting = false;
    },
    async completeReading() {
      if (!this.sessionId || this.completed) return;

      this.completing = true;
      try {
        await bibleStudyService.concluirLeitura(this.sessionId);
        this.completed = true;
        this.$toast.success('Leitura concluida.');
      } catch (error) {
        console.error('Erro ao concluir leitura biblica:', error);
        this.$toast.error('Nao foi possivel concluir a leitura.');
      } finally {
        this.completing = false;
      }
    },
    trackClientEvent(clientEventId) {
      if (!clientEventId) return;
      this.pendingClientEventIds.add(clientEventId);
      setTimeout(() => {
        this.pendingClientEventIds.delete(clientEventId);
      }, 15000);
    },
    isOwnClientEvent(clientEventId) {
      return !!clientEventId && this.pendingClientEventIds.has(clientEventId);
    },
    cleanupWebSocket() {
      clearTimeout(this.noteTimer);
      this.unsubscribeFns.forEach((unsubscribe) => unsubscribe());
      this.unsubscribeFns = [];
      this.wsClient?.disconnect();
      this.wsClient = null;
      this.enteredStudy = false;
      this.websocketStatus = 'DISCONNECTED';
    },
    cleanup() {
      if (this.enteredStudy) {
        this.sendPresence('LEAVE_SESSION');
      }
      this.webRtcClient?.end();
      this.webRtcClient = null;
      this.localStream = null;
      this.remoteStream = null;
      this.cleanupWebSocket();
    },
  },
};
</script>

<style scoped>
.bible-page {
  min-height: calc(100vh - 72px);
  padding: 22px;
  background:
    linear-gradient(180deg, rgba(249, 246, 239, 0.92), rgba(236, 242, 241, 0.96)),
    #f7f3ea;
}

.bible-page__wrap {
  max-width: 1440px;
  margin: 0 auto;
}

.bible-state {
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #675f53;
  font-weight: 700;
}

.study-picker {
  min-height: calc(100vh - 116px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 28px 0;
}

.study-picker__hero {
  width: 100%;
  max-width: 760px;
  text-align: center;
  color: #443a30;
}

.study-picker__mark {
  width: 64px;
  height: 64px;
  margin: 0 auto 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #edf3e9;
  border: 1px solid rgba(111, 128, 99, 0.2);
  box-shadow: 0 10px 26px rgba(71, 59, 42, 0.08);
}

.study-picker__hero > span,
.study-picker__card-header span,
.study-picker__suggestion span {
  display: block;
  color: #8b6d32;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.study-picker__hero h1 {
  margin: 8px auto 8px;
  max-width: 720px;
  color: #3f372e;
  font-size: clamp(1.6rem, 4vw, 2.8rem);
  line-height: 1.12;
  letter-spacing: 0;
}

.study-picker__hero p {
  margin: 0;
  color: #74695c;
  font-size: 1rem;
}

.study-picker__grid {
  width: 100%;
  max-width: 920px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 18px;
  align-items: stretch;
}

.study-picker__card,
.study-picker__suggestion {
  border-radius: 8px;
  border: 1px solid rgba(111, 128, 99, 0.18);
  background: rgba(255, 253, 248, 0.94);
  box-shadow: 0 16px 38px rgba(71, 59, 42, 0.08);
}

.study-picker__card {
  padding: 22px;
}

.study-picker__card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
}

.study-picker__card-header h2,
.study-picker__suggestion h3 {
  margin: 4px 0 0;
  color: #433b32;
  letter-spacing: 0;
}

.study-picker__field ::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.78) !important;
  border-radius: 8px;
}

.study-picker__button,
.study-picker__suggestion-button {
  border-radius: 8px;
  letter-spacing: 0;
  text-transform: none;
}

.study-picker__suggestion {
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.study-picker__suggestion p {
  margin: 8px 0 18px;
  color: #756c60;
  line-height: 1.55;
}

.start-panel,
.entry-panel {
  margin-top: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid rgba(111, 128, 99, 0.18);
  background: #fffdf8;
  box-shadow: 0 12px 30px rgba(71, 59, 42, 0.07);
}

.start-panel__content {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.start-panel__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #edf3e9;
  flex-shrink: 0;
}

.start-panel__eyebrow,
.entry-panel span {
  display: block;
  color: #8b6d32;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.start-panel h2 {
  margin: 3px 0;
  color: #433b32;
  font-size: 1.4rem;
  letter-spacing: 0;
}

.start-panel p,
.entry-panel p {
  margin: 0;
  color: #756c60;
}

.start-panel__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.start-panel .v-btn,
.entry-panel .v-btn {
  border-radius: 8px;
  letter-spacing: 0;
  text-transform: none;
}

.room-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 18px;
  align-items: start;
  margin-top: 18px;
}

.room-main {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
}

.room-section {
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(111, 128, 99, 0.14);
  background: rgba(255, 253, 248, 0.72);
}

.room-section__title {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.room-section__title span {
  color: #433b32;
  font-size: 1rem;
  font-weight: 700;
}

.room-section__title small {
  color: #7d7468;
  font-weight: 600;
}

.notes-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

@media (max-width: 1180px) {
  .room-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .bible-page {
    min-height: calc(100vh - 56px);
    padding: 12px;
  }

  .start-panel,
  .entry-panel {
    flex-direction: column;
    align-items: stretch;
  }

  .study-picker {
    min-height: auto;
    padding: 18px 0;
  }

  .study-picker__grid {
    grid-template-columns: 1fr;
  }

  .study-picker__card,
  .study-picker__suggestion {
    padding: 16px;
  }

  .start-panel__actions,
  .start-panel .v-btn,
  .entry-panel .v-btn {
    width: 100%;
  }

  .notes-grid {
    grid-template-columns: 1fr;
  }

  .room-section {
    padding: 12px;
  }

  .room-section__title {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
}
</style>
